import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";

import { formatSessionTime, groupSessions } from "../../utils/sessions";

/**
 * One row per person, not per token. Sessions stopped evicting each other
 * when the roles work made revocation per-session, so anyone working across
 * two apps and a phone holds several at once — which turned this table into
 * the same name twenty times over. The individual sessions expand; logging
 * out was already per person.
 */
export default function SessionsTable({ sessions, loading, onLogoutClick }) {
  const [expanded, setExpanded] = useState(() => new Set());
  const rows = useMemo(() => groupSessions(sessions), [sessions]);

  const toggle = (key) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  if (loading) {
    return (
      <Box py={4} display="flex" justifyContent="center">
        <CircularProgress size={28} />
      </Box>
    );
  }

  return (
    <TableContainer component={Paper} variant="outlined">
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={{ width: 44 }} />
            <TableCell>User</TableCell>
            <TableCell>Email</TableCell>
            <TableCell>Role</TableCell>
            <TableCell>Sessions</TableCell>
            <TableCell>Latest sign-in</TableCell>
            <TableCell align="right">Action</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => {
            const open = expanded.has(row.key);
            const expired = row.total - row.activeCount;
            return (
              <React.Fragment key={row.key}>
                <TableRow hover>
                  <TableCell>
                    <IconButton size="small" onClick={() => toggle(row.key)} aria-label="Show sessions">
                      {open ? (
                        <KeyboardArrowDownIcon fontSize="small" />
                      ) : (
                        <KeyboardArrowRightIcon fontSize="small" />
                      )}
                    </IconButton>
                  </TableCell>
                  <TableCell>{row.name}</TableCell>
                  <TableCell>{row.email}</TableCell>
                  <TableCell>{row.role || "-"}</TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Chip
                        size="small"
                        label={
                          row.activeCount
                            ? `${row.activeCount} active`
                            : "None active"
                        }
                        color={row.activeCount ? "success" : "default"}
                      />
                      {expired > 0 && (
                        <Typography variant="caption" sx={{ opacity: 0.7 }}>
                          +{expired} ended
                        </Typography>
                      )}
                    </Stack>
                  </TableCell>
                  <TableCell>{formatSessionTime(row.latestActiveAt)}</TableCell>
                  <TableCell align="right">
                    <Button
                      size="small"
                      color="warning"
                      disabled={!row.activeCount}
                      onClick={() => onLogoutClick(row.sessions[0])}
                      sx={{ height: 32, whiteSpace: "nowrap" }}
                    >
                      Log out all
                    </Button>
                  </TableCell>
                </TableRow>

                <TableRow>
                  <TableCell sx={{ py: 0, borderBottom: open ? undefined : "none" }} colSpan={7}>
                    <Collapse in={open} unmountOnExit>
                      <Box sx={{ py: 1, pl: 5 }}>
                        <Table size="small">
                          <TableBody>
                            {row.sessions.map((s) => (
                              <TableRow key={s.id}>
                                <TableCell sx={{ border: 0, width: 180 }}>
                                  {formatSessionTime(s.createdAt)}
                                </TableCell>
                                <TableCell sx={{ border: 0 }}>{s.client || "—"}</TableCell>
                                <TableCell sx={{ border: 0, width: 120 }}>
                                  <Chip
                                    size="small"
                                    label={s.active ? "Active" : "Ended"}
                                    color={s.active ? "success" : "default"}
                                  />
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </Box>
                    </Collapse>
                  </TableCell>
                </TableRow>
              </React.Fragment>
            );
          })}

          {rows.length === 0 && (
            <TableRow>
              <TableCell colSpan={7} align="center">
                No session records found
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
